import type { CommandBridge } from '@/bridge/CommandBridge'
import {
    createDisposer,
    createLogPrefix,
    type Disposable,
    EventHostImpl,
    logDebug,
    type LogTarget,
    onAny,
    throttle,
} from '@amazon/vinyl-util'
import {
    BridgeDelegate,
    type BridgeDelegateOptions,
    type PropDelegate,
} from '@/bridge/BridgeDelegate'
import type { Player } from '@/playback/api'
import type { QualitySelectionReason, TrackNerdStats } from '@amzn/vinyl-amzn'
import type {
    AmazonPlaybackQuality,
    VinylAmznTrackLoadOptions,
    VinylPlayerEventMap,
} from '@amzn/vinyl-amzn'

const ALL_STREAMING_EVENTS = [
    'contentTypesChange',
    'fetchedRangesChange',
    'streamingQualityChange',
    'bufferingQualityChange',
    'playbackQualityChange',
    'bufferingEnded',
    'error',
    'reset',
    'qualitiesChange',
    'qualitiesUnfilteredChange',
] as const

const ALL_PLAYBACK_STATE_EVENTS = [
    'abort',
    'canPlay',
    'canPlayThrough',
    'durationChange',
    'emptied',
    'ended',
    'error',
    'playIsPendingChange',
    'loadedData',
    'loadedMetadata',
    'loadStart',
    'looped',
    'mutedChange',
    'pause',
    'play',
    'playRejected',
    'played',
    'playing',
    'progress',
    'rateChange',
    'readyStateChange',
    'reset',
    'seeked',
    'seeking',
    'timeUpdate',
    'volumeChange',
    'waited',
    'waiting',
    'waitingForKey',
] as const

export interface BridgedPlayerDeps {
    readonly bridge: CommandBridge
    readonly player: Player
}

/**
 * When the queue is set, transmits over the bridge for the receiver to begin playback.
 */
export class BridgedPlayer
    extends EventHostImpl<VinylPlayerEventMap<VinylAmznTrackLoadOptions>>
    implements Player, LogTarget, Disposable
{
    logPrefix = createLogPrefix('BridgedPlayer')

    readonly clearQueue: Player['clearQueue']
    readonly configure: Player['configure']
    readonly load: Player['load']
    readonly pause: Player['pause']
    readonly play: Player['play']
    readonly preload: Player['preload']
    readonly seekTo: Player['seekTo']
    readonly setStreamingQuality: Player['setStreamingQuality']
    readonly unload: Player['unload']
    private _canPlay: PropDelegate<Player, 'canPlay'>
    private _canPlayThrough: PropDelegate<Player, 'canPlayThrough'>
    private _currentTime: PropDelegate<Player, 'currentTime'>
    private _currentTimePercent: PropDelegate<Player, 'currentTimePercent'>
    private _duration: PropDelegate<Player, 'duration'>
    private _paused: PropDelegate<Player, 'paused'>
    private _playing: PropDelegate<Player, 'playing'>
    private _ended: PropDelegate<Player, 'ended'>
    private _hasMetadata: PropDelegate<Player, 'hasMetadata'>
    private _fetchedTimePercent: PropDelegate<Player, 'fetchedTimePercent'>
    private _loop: PropDelegate<Player, 'loop'>
    private _muted: PropDelegate<Player, 'muted'>
    private _volume: PropDelegate<Player, 'volume'>
    private _playbackRate: PropDelegate<Player, 'playbackRate'>
    private _playbackQualityAmzn: PropDelegate<Player, 'playbackQualityAmzn'>
    private _trackNerdStats: PropDelegate<Player, 'trackNerdStats'>

    get canPlay(): boolean {
        return this._canPlay.get()
    }

    get canPlayThrough(): boolean {
        return this._canPlayThrough.get()
    }

    get currentTime(): number {
        return this._currentTime.get()
    }

    get currentTimePercent(): number {
        return this._currentTimePercent.get()
    }

    get duration(): number {
        return this._duration.get()
    }

    get paused(): boolean {
        return this._paused.get()
    }

    get playing(): boolean {
        return this._playing.get()
    }

    get ended(): boolean {
        return this._ended.get()
    }

    get hasMetadata(): boolean {
        return this._hasMetadata.get()
    }

    get fetchedTimePercent(): number {
        return this._fetchedTimePercent.get()
    }

    get loop(): boolean {
        return this._loop.get()
    }

    set loop(value: boolean) {
        this._loop.set(value)
    }

    get muted(): boolean {
        return this._muted.get()
    }

    set muted(value: boolean) {
        this._muted.set(value)
    }

    get volume(): number {
        return this._volume.get()
    }

    set volume(value: number) {
        this._volume.set(value)
    }

    get playbackRate(): number {
        return this._playbackRate.get()
    }

    set playbackRate(value: number) {
        this._playbackRate.set(value)
    }

    get playbackQualityAmzn(): AmazonPlaybackQuality | null {
        return this._playbackQualityAmzn.get()
    }

    get trackNerdStats(): TrackNerdStats | null {
        return this._trackNerdStats.get()
    }

    private readonly disposer = createDisposer()

    constructor(options: BridgeDelegateOptions<Player>) {
        super()
        const { add } = this.disposer
        const player = options.localController
        const bridgeDelegate = add(new BridgeDelegate<Player>(options))

        this.clearQueue = bridgeDelegate.createCommand('clearQueue')
        this.configure = bridgeDelegate.createCommand('configure')
        this.load = bridgeDelegate.createCommand('load')
        this.pause = bridgeDelegate.createCommand('pause')
        this.play = bridgeDelegate.createAsyncCommand('play')
        this.preload = bridgeDelegate.createCommand('preload')
        this.seekTo = bridgeDelegate.createAsyncCommand('seekTo')
        this.setStreamingQuality = bridgeDelegate.createCommand('setStreamingQuality')
        this.unload = bridgeDelegate.createCommand('unload')

        this._canPlay = bridgeDelegate.createProperty('canPlay', false)
        this._canPlayThrough = bridgeDelegate.createProperty('canPlayThrough', false)
        this._currentTime = bridgeDelegate.createProperty('currentTime', 0)
        this._currentTimePercent = bridgeDelegate.createProperty('currentTimePercent', 0)
        this._duration = bridgeDelegate.createProperty('duration', Number.NaN)
        this._ended = bridgeDelegate.createProperty('ended', false)
        this._paused = bridgeDelegate.createProperty('paused', false)
        this._playing = bridgeDelegate.createProperty('playing', false)
        this._hasMetadata = bridgeDelegate.createProperty('hasMetadata', false)
        this._fetchedTimePercent = bridgeDelegate.createProperty('fetchedTimePercent', 0)
        this._loop = bridgeDelegate.createProperty('loop', false)
        this._muted = bridgeDelegate.createProperty('muted', false)
        this._volume = bridgeDelegate.createProperty('volume', 1)
        this._playbackQualityAmzn = bridgeDelegate.createProperty('playbackQualityAmzn', null)
        this._trackNerdStats = bridgeDelegate.createProperty('trackNerdStats', null)
        this._playbackRate = bridgeDelegate.createProperty('playbackRate', 1)

        if (player) {
            add(
                player.on('readyStateChange', () => {
                    this._canPlay.update()
                    this._canPlayThrough.update()
                    this._hasMetadata.update()
                })
            )
            add(
                player.on(
                    'timeUpdate',
                    throttle(
                        () => {
                            this._currentTime.update()
                            this._currentTimePercent.update()
                        },
                        0.2,
                        {
                            leading: true,
                            trailing: true,
                        }
                    )
                )
            )
            add(
                player.on('durationChange', () => {
                    this._duration.update()
                })
            )
            add(
                player.on('volumeChange', () => {
                    this._volume.update()
                })
            )
            add(
                player.on('mutedChange', () => {
                    this._muted.update()
                })
            )
            add(
                onAny(player, ['pause', 'play', 'playing', 'played', 'ended'], () => {
                    this._paused.update()
                    this._playing.update()
                    this._ended.update()
                })
            )
            add(
                player.on('playbackQualityChange', () => {
                    this._playbackQualityAmzn.update()
                    this._trackNerdStats.update()
                })
            )
        }
        bridgeDelegate.createEventHostDelegate(this, [
            ...ALL_PLAYBACK_STATE_EVENTS,
            // ...ALL_TRACK_CONTROLLER_EVENTS,
            ...ALL_STREAMING_EVENTS,
        ])
        logDebug(this, 'construct')
    }

    private _qualitySelectionReason: QualitySelectionReason | null = null

    getQualitySelectionReason(): QualitySelectionReason | null {
        return this._qualitySelectionReason
    }

    dispose() {
        logDebug(this, 'dispose')
        this.disposer.dispose()
    }
}
